import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { listTeams, createTeam, type Team, type CreateTeamData } from '../../api/teams';
import { DataTable, type Column } from '../common/DataTable';
import { Modal } from '../common/Modal';

export function TeamsView() {
  const { getJwtToken } = useAuth();
  const navigate = useNavigate();

  const [teams, setTeams] = useState<Team[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showAddModal, setShowAddModal] = useState(false);

  const fetchTeams = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const token = await getJwtToken();
      if (!token) return;
      const data = await listTeams(token);
      setTeams(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load teams');
    } finally {
      setLoading(false);
    }
  }, [getJwtToken]);

  useEffect(() => {
    fetchTeams();
  }, [fetchTeams]);

  const handleCreate = async (data: CreateTeamData) => {
    const token = await getJwtToken();
    if (!token) return;
    await createTeam(data, token);
    setShowAddModal(false);
    await fetchTeams();
  };

  const columns: Column<Team>[] = [
    { key: 'name', header: 'Name' },
    { key: 'description', header: 'Description' },
    {
      key: 'members',
      header: 'Members',
      render: (t) => String(t.members?.length ?? 0),
    },
    {
      key: 'maxBudget',
      header: 'Budget',
      render: (t) => t.maxBudget != null ? `$${t.maxBudget.toFixed(2)}` : '--',
    },
    {
      key: 'createdAt',
      header: 'Created',
      render: (t) => new Date(t.createdAt).toLocaleDateString(),
    },
  ];

  return (
    <div>
      <div className="view__header">
        <h1 className="view__title">Teams</h1>
        <div className="view__actions">
          <button
            className="btn btn--primary"
            onClick={() => setShowAddModal(true)}
            type="button"
          >
            + Add Team
          </button>
          <button
            className="btn btn--secondary"
            onClick={fetchTeams}
            disabled={loading}
            type="button"
          >
            Refresh
          </button>
        </div>
      </div>

      {error && (
        <div className="alert alert--error">
          <span>{error}</span>
          <button onClick={() => setError(null)} type="button">Dismiss</button>
        </div>
      )}

      {loading && teams.length === 0 ? (
        <div className="view__empty">Loading teams...</div>
      ) : (
        <DataTable<Team>
          columns={columns}
          data={teams}
          keyExtractor={(t) => t.teamId}
          onRowClick={(t) => navigate(`/admin/teams/${t.teamId}`)}
          emptyMessage="No teams found. Click 'Add Team' to create one."
        />
      )}

      <Modal
        isOpen={showAddModal}
        onClose={() => setShowAddModal(false)}
        title="Add Team"
        wide
      >
        <TeamForm
          onSubmit={handleCreate}
          onCancel={() => setShowAddModal(false)}
        />
      </Modal>
    </div>
  );
}

/* ---------- inline form for creating/editing a team ---------- */

interface TeamFormProps {
  team?: Team;
  isEdit?: boolean;
  onSubmit: (data: CreateTeamData) => Promise<void>;
  onCancel: () => void;
}

function TeamForm({ team, isEdit, onSubmit, onCancel }: TeamFormProps) {
  const [name, setName] = useState(team?.name ?? '');
  const [description, setDescription] = useState(team?.description ?? '');
  const [maxBudget, setMaxBudget] = useState(team?.maxBudget?.toString() ?? '');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    try {
      await onSubmit({
        name,
        description,
        maxBudget: maxBudget ? Number(maxBudget) : undefined,
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save team');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit}>
      {error && (
        <div className="alert alert--error" style={{ marginBottom: 12 }}>
          <span>{error}</span>
        </div>
      )}

      <div className="form-group">
        <label className="form-group__label">Name</label>
        <input
          className="form-group__input"
          value={name}
          onChange={(e) => setName(e.target.value)}
          required
          placeholder="e.g. Engineering"
        />
      </div>

      <div className="form-group">
        <label className="form-group__label">Description</label>
        <input
          className="form-group__input"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          required
          placeholder="e.g. Core engineering team"
        />
      </div>

      <div className="form-group">
        <label className="form-group__label">Max Budget ($)</label>
        <input
          className="form-group__input"
          type="number"
          step="0.01"
          min="0"
          value={maxBudget}
          onChange={(e) => setMaxBudget(e.target.value)}
          placeholder="Leave empty for unlimited"
        />
      </div>

      <div className="modal__actions">
        <button type="button" className="modal__cancel" onClick={onCancel}>
          Cancel
        </button>
        <button type="submit" className="modal__submit" disabled={submitting}>
          {submitting ? 'Saving...' : isEdit ? 'Update Team' : 'Create Team'}
        </button>
      </div>
    </form>
  );
}
